import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { ShareNetwork, DownloadSimple, ArrowsClockwise, Sparkle } from "@phosphor-icons/react";

/**
 * StoryOfTheDay — daily 1080×1920 PNG suggestion for WhatsApp Status / IG Story.
 *
 * Server compone l'immagine con la prima foto disponibile dell'agente, un
 * gradient sage in basso, e prezzo + indirizzo. Il bottone "Condividi" usa
 * Web Share API (con file fallback su download).
 */
export default function StoryOfTheDay({ hideWhenEmpty = false }) {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);   // {available, property, image_url}
  const [loading, setLoading] = useState(true);
  const [pngBlobUrl, setPngBlobUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const blobUrlRef = useRef(null);

  const fetchMeta = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/story/today`);
      setMeta(data);
    } catch (err) {
      setMeta({ available: false, reason: "Non sono riuscito a recuperare la storia di oggi." });
    } finally {
      setLoading(false);
    }
  };

  const fetchPng = async () => {
    if (blobUrlRef.current) return blobUrlRef.current;
    try {
      const resp = await api.get(`/story/today.png`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      blobUrlRef.current = url;
      setPngBlobUrl(url);
      return url;
    } catch (err) {
      toast.error("Non sono riuscito a generare l'immagine. Riprova.");
      return null;
    }
  };

  useEffect(() => {
    fetchMeta();
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  // Pre-fetch the PNG once we know we have a property
  useEffect(() => {
    if (meta?.available && !pngBlobUrl) {
      fetchPng();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.available]);

  const onShare = async () => {
    setBusy(true);
    try {
      const url = pngBlobUrl || await fetchPng();
      if (!url) return;
      const blob = await (await fetch(url)).blob();
      const file = new File([blob], "storia-pronto.png", { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: "Pronto · Storia del giorno",
          text: meta?.property?.title || "",
        });
      } else {
        // Fallback: download
        const a = document.createElement("a");
        a.href = url;
        a.download = "storia-pronto.png";
        a.click();
        toast.success("Immagine scaricata. Aprila su WhatsApp Status.");
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("Non sono riuscito a condividere. Prova a scaricarla.");
      }
    } finally {
      setBusy(false);
    }
  };

  const onDownload = async () => {
    setBusy(true);
    const url = pngBlobUrl || await fetchPng();
    if (url) {
      const a = document.createElement("a");
      a.href = url;
      a.download = "storia-pronto.png";
      a.click();
    }
    setBusy(false);
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7E0] rounded-xl p-5 sm:p-6" data-testid="story-card-loading">
        <div className="h-3 w-32 bg-[#EDEAE2] rounded mb-3 animate-pulse" />
        <div className="h-4 w-48 bg-[#EDEAE2] rounded mb-5 animate-pulse" />
        <div className="aspect-[9/16] max-w-[180px] bg-[#EDEAE2] rounded-lg animate-pulse" />
      </div>
    );
  }

  if (!meta?.available) {
    if (hideWhenEmpty) return null;
    return (
      <div className="bg-white border border-[#E5E7E0] rounded-xl p-5 sm:p-6" data-testid="story-card-empty">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">La storia di oggi</span>
        </div>
        <p className="text-[14px] text-[#6B6A65] leading-snug">
          {meta?.reason || "Pubblica almeno un immobile con una foto e Pronto te lo proporrà come storia per WhatsApp."}
        </p>
        <button
          onClick={() => nav("/dashboard/property/new")}
          className="mt-3 text-[12px] uppercase tracking-[0.18em] text-[#7C8B6E] hover:underline"
        >
          Crea il primo annuncio →
        </button>
      </div>
    );
  }

  const p = meta.property || {};
  const priceFmt = p.price ? `€ ${Number(p.price).toLocaleString("it-IT")}` : "Su richiesta";

  return (
    <div className="bg-white border border-[#E5E7E0] rounded-xl p-5 sm:p-6" data-testid="story-card">
      <div className="flex items-center gap-2 mb-1">
        <Sparkle size={13} weight="fill" className="text-amber-600" />
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#7C8B6E] font-semibold">La storia di oggi</span>
      </div>
      <h4 className="font-editorial text-[18px] sm:text-[20px] text-[#1C1C1A] leading-tight mb-1">
        Pronto da pubblicare su <span className="italic-accent text-[#7C8B6E]">WhatsApp Status</span>.
      </h4>
      <p className="text-[12.5px] text-[#6B6A65] mb-4 leading-snug">
        {p.title || "Immobile in evidenza"} · {priceFmt}
      </p>

      <div className="flex gap-4">
        {/* Preview 9:16 */}
        <div className="flex-shrink-0 w-[120px] sm:w-[140px] aspect-[9/16] rounded-lg overflow-hidden bg-[#F1F4ED] border border-[#E5E7E0] relative" data-testid="story-preview">
          {pngBlobUrl ? (
            <img src={pngBlobUrl} alt="Anteprima storia" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <div className="w-7 h-7 rounded-full border-3 border-[#7C8B6E] border-t-transparent animate-spin" />
            </div>
          )}
        </div>

        <div className="flex-1 flex flex-col gap-2.5 justify-center">
          <button
            onClick={onShare}
            disabled={busy || !pngBlobUrl}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-[#7C8B6E] hover:bg-[#90A082] text-white text-[13px] font-semibold transition-colors disabled:opacity-50"
            data-testid="story-share-btn"
          >
            <ShareNetwork size={15} weight="fill" /> Condividi
          </button>
          <button
            onClick={onDownload}
            disabled={busy || !pngBlobUrl}
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md bg-white border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#F1F4ED] text-[13px] font-medium transition-colors disabled:opacity-50"
            data-testid="story-download-btn"
          >
            <DownloadSimple size={15} weight="bold" /> Scarica
          </button>
          <button
            onClick={() => { if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current); blobUrlRef.current = null; setPngBlobUrl(null); fetchMeta(); }}
            disabled={busy}
            className="w-full text-[11px] uppercase tracking-[0.18em] text-[#6B6A65] hover:text-[#1C1C1A] flex items-center justify-center gap-1 py-1"
            data-testid="story-refresh-btn"
          >
            <ArrowsClockwise size={11} weight="bold" /> Suggerisci un altro
          </button>
        </div>
      </div>
    </div>
  );
}
